import { useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { BOOKING_URL } from "@/lib/booking";

const EASE = [0.22, 1, 0.36, 1];

export function Reveal({ children, className = "", delay = 0, y = 24, as = "div" }) {
  const Tag = motion[as] || motion.div;
  const reduceMotion = useReducedMotion();
  const initial = useMemo(() => reduceMotion ? false : { opacity: 0, y }, [reduceMotion, y]);
  const transition = useMemo(() => ({ duration: reduceMotion ? 0 : 0.9, ease: EASE, delay: reduceMotion ? 0 : delay }), [reduceMotion, delay]);
  return (
    <Tag initial={initial} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={transition} className={className}>
      {children}
    </Tag>
  );
}

export function ImageReveal({ src, alt = "", className = "", imgClassName = "" }) {
  const reduceMotion = useReducedMotion();
  const initial = useMemo(() => reduceMotion ? false : { clipPath: "inset(7% 0 7% 0)", opacity: 0 }, [reduceMotion]);
  const transition = useMemo(() => ({ duration: reduceMotion ? 0 : 1.1, ease: EASE }), [reduceMotion]);
  return (
    <motion.div initial={initial} whileInView={{ clipPath: "inset(0 0 0 0)", opacity: 1 }} viewport={{ once: true, margin: "-60px" }} transition={transition} className={`overflow-hidden ${className}`}>
      <img src={src} alt={alt} loading="lazy" className={`w-full h-full object-cover ${imgClassName}`} />
    </motion.div>
  );
}

export function PublicLayout({ children, overlay = false, stickyBook = false }) {
  const reduceMotion = useReducedMotion();
  const initial = useMemo(() => reduceMotion ? false : { opacity: 0 }, [reduceMotion]);
  const transition = useMemo(() => ({ duration: reduceMotion ? 0 : 0.55, ease: EASE }), [reduceMotion]);
  const protectImage = (event) => {
    if (event.target instanceof Element && event.target.closest("img")) event.preventDefault();
  };
  return (
    <motion.div initial={initial} animate={{ opacity: 1 }} transition={transition} onContextMenuCapture={protectImage} onDragStartCapture={protectImage} className="public-shell min-h-screen overflow-x-hidden">
      <Navbar overlay={overlay} />
      <main>{children}</main>
      <Footer />
      {stickyBook && (
        <div className="md:hidden fixed bottom-0 inset-x-0 z-40 p-4 bg-[#0B0C0E]/95 backdrop-blur-xl border-t border-white/10">
          <a href={BOOKING_URL} data-testid="sticky-book-btn" className="btn-dark w-full">Book a Shoot</a>
        </div>
      )}
    </motion.div>
  );
}

export function PageIntro({ label, title, text, children }) {
  return (
    <section className="px-6 sm:px-10 lg:px-16 pt-40 pb-20 md:pb-28 border-b border-white/10">
      <Reveal>
        {label && <div className="eyebrow">{label}</div>}
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 max-w-4xl leading-[.95] text-[#F4F4F6]">{title}</h1>
        {text && <p className="text-sm md:text-lg text-zinc-400 mt-7 max-w-xl leading-relaxed">{text}</p>}
        {children}
      </Reveal>
    </section>
  );
}

export function FinalCta({ image, title = "Ready for your next listing?" }) {
  return (
    <section data-testid="final-cta" className="relative overflow-hidden border-t border-white/10">
      {image && <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-35" />}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B0C0E] via-[#0B0C0E]/78 to-[#0B0C0E]/35" />
      <div className="relative px-6 sm:px-10 lg:px-16 py-28 md:py-44">
        <Reveal>
          <div className="eyebrow-light mb-6">Mandalay Real Estate Media</div>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl max-w-3xl leading-[.95] text-white">{title}</h2>
          <div className="flex flex-wrap gap-4 mt-12">
            <a href={BOOKING_URL} data-testid="cta-book-btn" className="btn-dark">Book a Shoot</a>
            <Link to="/contact" data-testid="cta-contact-btn" className="btn-outline">Contact Us</Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
